import { styled } from "styled-components";
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Select,
  UploadFile,
} from "antd";
import { useForm } from "antd/es/form/Form";
import {
  MdCheck,
  MdCheckCircleOutline,
  MdLink,
  MdRemove,
  MdShare,
} from "react-icons/md";
import { colors } from "../../../theme/colors";
import { useCallback, useEffect, useState } from "react";
import { debounceTime, Subject } from "rxjs";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Splash } from "../../../components/ui/splash/splash.component";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SubmitButton } from "../../../components/layout/submit-button/submit-button.component";
import { AxiosError } from "axios";
import { handleResponse } from "../../../utils/functions/http/handleResponse";
import toast from "react-hot-toast";
import { ProfessionalProfile } from "../../../models/professional-profile.model";
import { ImageUploader } from "../../../components/ui/image-uploader/image-uploader.component";
import { axios } from "../../../api/axios";
import { useAtomValue } from "jotai";
import { authAtom } from "../../../atoms/auth.atom";
import { sluggify } from "../../../utils/functions/string/sluggify";
import {
  CUSTOM_URL_MAX_LENGTH,
  isInvalidCustomUrl,
  sanitizeCustomUrl,
} from "../../../utils/functions/string/custom-url";
import { masterAtom } from "../../../atoms/masters.atom";
import moment from "moment";
import { masterToOptions } from "../../../utils/functions/misc/masterToOptions";
import dayjs from "dayjs";
import { Pressable } from "../../../components/ui/pressable/pressable.component";
import { TextAreaResize } from "../../../components/ui/textarea-resize/textarea-resize.component";
import { Page } from "../../../components/layout/page/page.component";
import { Card } from "../../../components/ui/card/card.component";
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaWhatsapp,
  FaXTwitter,
} from "react-icons/fa6";
import { wait } from "../../../utils/functions/misc/wait";

const customUrlSubject$ = new Subject<string>();

type SocialPlatform = "facebook" | "linkedin" | "instagram" | "whatsapp" | "x";

const SOCIAL_FIELDS: {
  platform: SocialPlatform;
  urlKey: `${SocialPlatform}_url`;
  label: string;
  prefix: string;
  placeholder: string;
  icon: JSX.Element;
}[] = [
  {
    platform: "facebook",
    urlKey: "facebook_url",
    label: "Facebook",
    prefix: "https://facebook.com/",
    placeholder: "your.handle",
    icon: <FaFacebook color="#1877F2" size={20} />,
  },
  {
    platform: "linkedin",
    urlKey: "linkedin_url",
    label: "LinkedIn",
    prefix: "https://linkedin.com/",
    placeholder: "in/your-handle",
    icon: <FaLinkedin color="#0A66C2" size={20} />,
  },
  {
    platform: "instagram",
    urlKey: "instagram_url",
    label: "Instagram",
    prefix: "https://instagram.com/",
    placeholder: "your.handle",
    icon: <FaInstagram color="#E1306C" size={20} />,
  },
  {
    platform: "whatsapp",
    urlKey: "whatsapp_url",
    label: "WhatsApp",
    prefix: "https://wa.me/",
    placeholder: "XXYYYYYYYYYY",
    icon: <FaWhatsapp color="#25D366" size={20} />,
  },
  {
    platform: "x",
    urlKey: "x_url",
    label: "X",
    prefix: "https://x.com/",
    placeholder: "your-handle",
    icon: <FaXTwitter size={20} />,
  },
];

export const CreateProfessionalProfile = ({
  editing,
}: {
  editing?: boolean;
}) => {
  const [searchParams] = useSearchParams();

  const auth = useAtomValue(authAtom);
  const master = useAtomValue(masterAtom);

  const navigate = useNavigate();

  const [showSuccess, setShowSuccess] = useState(false);

  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePictureURL, setProfilePictureURL] = useState<string | null>(
    null
  );
  const [urlVerified, setUrlVerified] = useState<boolean | null>(null);
  const [showVerification, setShowVerification] = useState(true);
  const [customUrl, setCustomUrl] = useState("");

  const [about, setAbout] = useState("");

  const [isCurrent, setIsCurrent] = useState<Record<number, boolean>>({});

  const [form] = useForm<ProfessionalProfile>();

  const onSubmit = useCallback(async () => {
    try {
      await form.validateFields();
      if (!profilePictureURL && !profilePicture) {
        toast.error("Please upload a profile picture!");
        form.setFields([
          { name: "logo", errors: ["Please upload a profile picture!"] },
        ]);
        return;
      }
      if (!about.trim()) {
        return form.setFields([
          {
            name: "about",
            value: about,
            errors: ["Please enter an about description!"],
          },
        ]);
      } else {
        form.setFields([{ name: "about", value: about, errors: [] }]);
      }

      const profile = { ...form.getFieldsValue() };

      for (const { platform, urlKey, prefix } of SOCIAL_FIELDS) {
        const v = profile[urlKey] as string | undefined;
        if (v && v.trim()) {
          profile[urlKey] = v.startsWith("http") ? v : `${prefix}${v}`;
        } else {
          delete profile[urlKey];
        }
      }

      profile.qualifications = (profile.qualifications ?? []).map(
        (qualification) => ({
          ...qualification,
          year: dayjs(qualification.year as any as string).year(),
        })
      );
      profile.experiences = (profile.experiences ?? []).map((experience) => {
        const newExperience = { ...experience };
        newExperience.start_date = newExperience.start_date
          ? dayjs(newExperience.start_date, "DD-MM-YYYY").format("DD-MM-YYYY")
          : Array.isArray(newExperience.period)
          ? (newExperience as any).period[0].format("DD-MM-YYYY")
          : newExperience.period?.format("DD-MM-YYYY");
        newExperience.end_date = newExperience.end_date
          ? dayjs(newExperience.end_date, "DD-MM-YYYY")?.format("DD-MM-YYYY")
          : Array.isArray(newExperience.period)
          ? (newExperience as any).period[1]?.format("DD-MM-YYYY")
          : newExperience.period?.format("DD-MM-YYYY");
        newExperience.end_date =
          newExperience.end_date ?? newExperience.start_date;
        delete (newExperience as any)["period"];
        return { ...newExperience };
      });

      profile.custom_username = profile.custom_url;
      delete (profile as any)["custom_url"];

      profile.about = about;

      const newProfileForm = new FormData();
      for (const key in profile) {
        if (key === "logo") continue;

        const value = profile[key as keyof typeof profile]!;
        if (Array.isArray(value)) {
          for (let i = 0; i < value.length; i++) {
            newProfileForm.append(
              key,
              (typeof value[i] === "string"
                ? value[i]
                : JSON.stringify(value[i])) as any
            );
          }
        } else {
          newProfileForm.append(key, value);
        }
      }
      if (profilePicture) {
        newProfileForm.append("logo", profilePicture);
      } else if (profilePictureURL) {
        newProfileForm.append("logo", profilePictureURL);
      }

      (editing ? axios.put : axios.post)(
        "/auth/professional/profile",
        newProfileForm,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
        .then((resp) =>
          handleResponse(resp, async () => {
            setShowSuccess(true);
            await wait(3000);
            navigate(searchParams.get("r") ?? "/professional", {
              replace: true,
            });
          })
        )
        .catch((error) => {
          if (error instanceof AxiosError) {
            let errorFields = error.response?.data ?? {};
            if (errorFields.message) errorFields = errorFields.message;

            let hasSetError = false;

            for (const key in errorFields) {
              if (form.getFieldInstance(key)) hasSetError = true;
              form.setFields([
                {
                  name: key,
                  errors: Array.isArray(errorFields[key])
                    ? errorFields[key]
                    : [errorFields[key]],
                },
              ]);
            }
            if (!hasSetError) {
              const message = Object.entries(errorFields)
                .map(
                  ([key, errors]: any) =>
                    `${key}: ${
                      (Array.isArray(errors) ? errors.join(",") : errors) ??
                      "Error"
                    }`
                )
                .join(";");

              toast.error(message);
            }
          } else {
            handleResponse(error);
          }
        });
    } catch (e) {
      if (e instanceof AxiosError) {
        handleResponse(e);
      } else {
        toast.error("Please fix the errors in the form!");
      }
    }
  }, [form, profilePicture, profilePictureURL, editing, about, searchParams]);

  useEffect(() => {
    axios
      .get<{
        profile: ProfessionalProfile;
        qualifications: ProfessionalProfile["qualifications"];
        experiences: ProfessionalProfile["experiences"];
      }>("/auth/professional/profile")
      .then(async (result) => {
        !editing && navigate("/p");
        if (editing) {
          const profile = {
            ...result.data.profile,
            qualifications: result.data.qualifications,
            experiences: result.data.experiences,
          } as ProfessionalProfile & Record<string, any>;
          if (profile.about) setAbout(profile.about);
          profile.qualifications = profile.qualifications.map(
            (qualification) => ({
              ...qualification,
              year: dayjs(qualification.year.toString()),
            })
          );
          profile.experiences = profile.experiences.map((experience) => ({
            ...experience,
            start_date: dayjs(experience.start_date, "DD-MM-YYYY"),
            end_date: dayjs(experience.end_date, "DD-MM-YYYY"),
          }));

          for (const { urlKey, prefix } of SOCIAL_FIELDS) {
            const v = profile[urlKey];
            if (v && typeof v === "string") {
              profile[urlKey] = v.replace(prefix, "");
            }
          }

          profile.custom_url = profile.custom_username;
          delete profile["custom_username"];

          if (profile.logo) {
            setProfilePictureURL(profile.logo);
          }
          if (profile.custom_url) setCustomUrl(profile.custom_url);

          form.setFieldsValue(profile);
          setIsCurrent(
            profile.experiences.reduce(
              (map, experience, index) => ({
                ...map,
                [index]: experience.is_current,
              }),
              {}
            )
          );
        }
      })
      .catch((e) => {
        if (e instanceof AxiosError && e.response?.status === 404)
          editing && navigate("/p", { replace: true });
        else {
          handleResponse(e);
        }
      });
  }, [form, editing]);

  useEffect(() => {
    if (auth.user && !editing) {
      const sluggifiedName = sluggify(
        `${auth.user.first_name} ${auth.user.last_name}`,
        ""
      );
      form.setFieldValue("custom_url", sluggifiedName);
      setCustomUrl(sluggifiedName);
    }
  }, [auth, editing, form]);

  useEffect(() => {
    const listener = customUrlSubject$
      .pipe(debounceTime(500))
      .subscribe((value) => {
        setCustomUrl(value);
      });

    return () => listener.unsubscribe();
  }, []);

  useEffect(() => {
    if (!customUrl) return;
    setShowVerification(false);

    axios
      .get(`/client/validate/custom-url?q=${customUrl}&category=professional`)
      .then(() => setUrlVerified(true))
      .catch(() => setUrlVerified(false))
      .finally(() => setShowVerification(true));
  }, [customUrl]);

  const shareProfile = async () => {
    const url = `https://distribute.pocketfilms.in/${
      form.getFieldValue("custom_url") ?? ""
    }`;

    if (
      (window.navigator as any).share &&
      window.navigator.canShare({ url })
    ) {
      try {
        await navigator.share({ url });
      } catch (e) {}
    } else {
      toast.success("Copied to clipboard!");
      window.navigator.clipboard.writeText(url);
    }
  };

  return (
    <Page>
      <Splash
        show={showSuccess}
        title={`Professional profile ${
          editing ? "saved" : "created"
        } successfully!`}
        icon={<MdCheckCircleOutline />}
      />
      <Wrap>
        <Form form={form} layout="vertical" requiredMark>
          <PageHeader>
            <PageTitle>
              {editing
                ? "Edit your professional profile"
                : "Create your professional profile"}
            </PageTitle>
            <PageSubtitle>
              Showcase your work, skills and experience to filmmakers and
              collaborators.
            </PageSubtitle>
          </PageHeader>

          <CardGrid>
            <SectionCard>
              <SectionHeader>
                <SectionTitleBlock>
                  <SectionTitle>About You</SectionTitle>
                  <SectionHint>
                    Your photo, public URL and a short bio.
                  </SectionHint>
                </SectionTitleBlock>
              </SectionHeader>

              <UrlFieldLabel>Public profile URL</UrlFieldLabel>
              <UrlControl>
                <UrlVerified
                  $verified={urlVerified}
                  $show={showVerification}
                  title={
                    urlVerified === null
                      ? "Checking..."
                      : urlVerified
                      ? "URL is available"
                      : "URL is taken"
                  }
                >
                  {urlVerified === null ? (
                    <MdLink color={colors.white.hex} size={16} />
                  ) : urlVerified ? (
                    <MdCheck color={colors.white.hex} size={16} />
                  ) : (
                    <MdRemove color={colors.white.hex} size={16} />
                  )}
                </UrlVerified>
                <UrlInputGroup>
                  <UrlPrefix>distribute.pocketfilms.in/</UrlPrefix>
                  <Form.Item
                    name="custom_url"
                    style={{ marginBottom: 0, flex: 1 }}
                    rules={[
                      {
                        validator: (_, value) => {
                          const v = `${value ?? ""}`;
                          if (!v.trim()) {
                            return Promise.reject(
                              new Error("Please enter a custom URL!")
                            );
                          }
                          if (v.length > CUSTOM_URL_MAX_LENGTH) {
                            return Promise.reject(
                              new Error(
                                `Max. limit is ${CUSTOM_URL_MAX_LENGTH} characters`
                              )
                            );
                          }
                          if (isInvalidCustomUrl(v)) {
                            return Promise.reject(
                              new Error(
                                "Only letters and numbers are allowed."
                              )
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <UrlInput
                      autoComplete="off"
                      name="custom_url"
                      maxLength={CUSTOM_URL_MAX_LENGTH}
                      placeholder="your-handle"
                      onInput={(e) => {
                        const v = sanitizeCustomUrl(
                          (e.target as HTMLInputElement).value
                        );
                        (e.target as HTMLInputElement).value = v;
                        form.setFieldValue("custom_url", v);
                        customUrlSubject$.next(v);
                        setCustomUrl(v);
                      }}
                    />
                  </Form.Item>
                </UrlInputGroup>
                <Pressable onClick={shareProfile}>
                  <ShareButton title="Share">
                    <MdShare color={colors.white.hex} size={16} />
                  </ShareButton>
                </Pressable>
              </UrlControl>

              <ProfileBody>
                <AvatarColumn>
                  <AvatarLabel>Profile picture</AvatarLabel>
                  <FormItem name="logo" required $noLabel>
                    <ImageUploader
                      name="logo"
                      url={profilePictureURL ? [profilePictureURL] : []}
                      action=""
                      onRemove={() => setProfilePictureURL(null)}
                      onComplete={(url: UploadFile[]) => {
                        setProfilePictureURL(url[0].response[0]);
                        setProfilePicture(url[0].response[1]);
                      }}
                    />
                  </FormItem>
                </AvatarColumn>
                <ProfileFields>
                  <FormItem
                    label="Key skills"
                    name="skills"
                    required
                    rules={[
                      { required: true, message: "Please select skills!" },
                    ]}
                  >
                    <Select
                      showSearch
                      options={masterToOptions("skill", master)}
                      mode="multiple"
                      placeholder="Select your key skills"
                    />
                  </FormItem>
                  <FormItem label="About Me" name="about" required>
                    <TextAreaResize
                      name="about"
                      placeholder="Say something about yourself"
                      rows={5}
                      value={about}
                      onChange={setAbout}
                    />
                  </FormItem>
                </ProfileFields>
              </ProfileBody>
            </SectionCard>

            <SectionCard>
              <SectionHeader>
                <SectionTitleBlock>
                  <SectionTitle>Social Media Links</SectionTitle>
                  <SectionHint>
                    Optional. Add the platforms you're on.
                  </SectionHint>
                </SectionTitleBlock>
              </SectionHeader>

              <SocialList>
                {SOCIAL_FIELDS.map(
                  ({ platform, urlKey, label, prefix, placeholder, icon }) => (
                    <SocialRow key={platform}>
                      <SocialBadge>
                        {icon}
                        <SocialLabel>{label}</SocialLabel>
                      </SocialBadge>
                      <FormItem
                        $noLabel
                        name={urlKey}
                        style={{ marginBottom: 0, flex: 1 }}
                      >
                        <Input
                          placeholder={placeholder}
                          prefix={
                            <SocialPrefix>{prefix}</SocialPrefix>
                          }
                        />
                      </FormItem>
                    </SocialRow>
                  )
                )}
              </SocialList>
            </SectionCard>

            <SectionCard>
              <Form.List name="qualifications">
                {(fields, { add, remove }, { errors }) => (
                  <>
                    <SectionHeader>
                      <SectionTitleBlock>
                        <SectionTitle>Qualifications</SectionTitle>
                        <SectionHint>
                          Add up to 5 educational qualifications.
                        </SectionHint>
                      </SectionTitleBlock>
                      {fields.length < 5 ? (
                        <AddButton
                          type="dashed"
                          onClick={() => add()}
                          icon={<PlusOutlined />}
                        >
                          Add Qualification
                        </AddButton>
                      ) : null}
                    </SectionHeader>
                    <ListWrap>
                      {fields.map(({ key, name }) => (
                        <EntryCard key={key}>
                          <EntryHeader>
                            <EntryIndex>Qualification</EntryIndex>
                            <RemoveBtn onClick={() => remove(name)}>
                              <MinusCircleOutlined />
                            </RemoveBtn>
                          </EntryHeader>
                          <Grid>
                            <FormItem
                              $span={2}
                              name={[name, "institute"]}
                              label="Institute"
                              rules={[
                                {
                                  required: true,
                                  message: "Enter Institute name",
                                },
                                {
                                  pattern: new RegExp("^[0-9a-zA-Z,. ]*$"),
                                  message:
                                    "Special characters allowed are . ,",
                                },
                              ]}
                            >
                              <Input placeholder="Institute Name" />
                            </FormItem>
                            <FormItem
                              name={[name, "degree"]}
                              label="Degree"
                              rules={[
                                {
                                  required: true,
                                  message: "Enter Degree name",
                                },
                                {
                                  pattern: new RegExp("^[0-9a-zA-Z. ]*$"),
                                  message: "No special characters allowed",
                                },
                              ]}
                            >
                              <Input placeholder="Degree Name" />
                            </FormItem>
                            <FormItem
                              name={[name, "year"]}
                              label="Year of passing"
                              rules={[
                                {
                                  required: true,
                                  message: "Enter Year of passing",
                                },
                              ]}
                            >
                              <DatePicker
                                format="YYYY"
                                picker="year"
                                placeholder="Year"
                                style={{ width: "100%" }}
                              />
                            </FormItem>
                          </Grid>
                        </EntryCard>
                      ))}

                      <Form.ErrorList errors={errors} />
                    </ListWrap>
                  </>
                )}
              </Form.List>
            </SectionCard>

            <SectionCard>
              <Form.List name="experiences">
                {(fields, { add, remove }, { errors }) => (
                  <>
                    <SectionHeader>
                      <SectionTitleBlock>
                        <SectionTitle>Work Experience</SectionTitle>
                        <SectionHint>
                          Add up to 5 jobs or freelance engagements.
                        </SectionHint>
                      </SectionTitleBlock>
                      {fields.length < 5 ? (
                        <AddButton
                          type="dashed"
                          onClick={() => add()}
                          icon={<PlusOutlined />}
                        >
                          Add Experience
                        </AddButton>
                      ) : null}
                    </SectionHeader>
                    <ListWrap>
                      {fields.map(({ key, name }, index) => (
                        <EntryCard key={key}>
                          <EntryHeader>
                            <EntryIndex>Experience</EntryIndex>
                            <RemoveBtn onClick={() => remove(name)}>
                              <MinusCircleOutlined />
                            </RemoveBtn>
                          </EntryHeader>
                          <Grid>
                            <FormItem
                              label="Type"
                              name={[name, "work_type"]}
                              rules={[
                                { required: true, message: "Select type" },
                              ]}
                            >
                              <Select
                                placeholder="Select type"
                                options={[
                                  { label: "Job", value: "job" },
                                  { label: "Freelance", value: "freelance" },
                                ]}
                                showSearch
                                allowClear
                              />
                            </FormItem>
                            <FormItem
                              label="Company"
                              name={[name, "company"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter the company name",
                                },
                                {
                                  pattern: new RegExp("^[0-9a-zA-Z.\\- ]*$"),
                                  message:
                                    "Only ,- are allowed as special characters",
                                },
                                {
                                  max: 100,
                                  message: "Max. limit is 100 characters",
                                },
                              ]}
                            >
                              <Input placeholder="Enter company name" />
                            </FormItem>
                            <FormItem
                              $span={2}
                              label="Role / Position"
                              name={[name, "position"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Select your Role / Position",
                                },
                              ]}
                            >
                              <Select
                                placeholder="Select your role / position"
                                showSearch
                                allowClear
                                options={masterToOptions(
                                  "company_role",
                                  master
                                )}
                              />
                            </FormItem>
                            <FormItem
                              label="Start date"
                              name={[name, "start_date"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Select start date!",
                                },
                              ]}
                            >
                              <DatePicker
                                format="DD-MM-YYYY"
                                style={{ width: "100%" }}
                                placeholder="Start date"
                                disabledDate={(date) =>
                                  date && +date > moment.now()
                                }
                              />
                            </FormItem>
                            {!isCurrent[index] ? (
                              <FormItem
                                label="End date"
                                name={[name, "end_date"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Select end date!",
                                  },
                                ]}
                              >
                                <DatePicker
                                  format="DD-MM-YYYY"
                                  style={{ width: "100%" }}
                                  placeholder="End date"
                                  disabledDate={(date) =>
                                    date && +date > moment.now()
                                  }
                                />
                              </FormItem>
                            ) : (
                              <FormItem label="End date" $disabled>
                                <DatePicker
                                  format="DD-MM-YYYY"
                                  style={{ width: "100%" }}
                                  placeholder="Present"
                                  disabled
                                />
                              </FormItem>
                            )}
                            <FormItem
                              $span={2}
                              name={[name, "is_current"]}
                              valuePropName="checked"
                              $noLabel
                            >
                              <Checkbox
                                onChange={(e) => {
                                  setIsCurrent({
                                    ...isCurrent,
                                    [index]: e.target.checked,
                                  });
                                }}
                              >
                                I currently work here
                              </Checkbox>
                            </FormItem>
                          </Grid>
                        </EntryCard>
                      ))}

                      <Form.ErrorList errors={errors} />
                    </ListWrap>
                  </>
                )}
              </Form.List>
            </SectionCard>
          </CardGrid>
        </Form>
      </Wrap>
      <SubmitButton
        label={editing ? "Save" : "Create"}
        onClick={() => {
          onSubmit();
        }}
      />
    </Page>
  );
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  padding: 0 0 12rem 0;

  .ant-form-item-has-error {
    margin-bottom: 3rem;
  }
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  width: 100%;
  margin-bottom: 0.4rem;
`;

const PageTitle = styled.h1`
  font-size: 2.4rem;
  font-weight: 600;
  margin: 0;
  color: ${colors.text.hex};
  line-height: 1.2;
`;

const PageSubtitle = styled.p`
  font-size: 1.4rem;
  color: ${colors.text.withOpacity(0.6).hex};
  margin: 0;
  line-height: 1.4;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2rem;
  width: 100%;
  align-items: stretch;

  @media (max-width: 60em) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SectionCard = styled(Card)`
  padding: 2rem 2.4rem;
  border: 1.5px solid ${colors.primary.withOpacity(0.3).hex};
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;
  margin-bottom: 1.6rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${colors.grey.hex};
`;

const SectionTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
  flex: 1;
`;

const SectionTitle = styled.h3`
  font-size: 1.7rem;
  font-weight: 600;
  margin: 0;
  color: ${colors.text.hex};
`;

const SectionHint = styled.span`
  font-size: 1.25rem;
  color: ${colors.text.withOpacity(0.6).hex};
  line-height: 1.4;
`;

const UrlFieldLabel = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${colors.text.withOpacity(0.65).hex};
  margin-bottom: 0.6rem;
`;

const UrlControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
  margin-bottom: 2rem;
`;

const UrlVerified = styled.div<{
  $verified?: boolean | null;
  $show?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  flex-shrink: 0;
  opacity: ${({ $show }) => ($show ? 1 : 0.4)};
  background-color: ${({ $verified }) =>
    $verified === null
      ? colors.primary.hex
      : $verified
      ? colors.success.hex
      : colors.error.hex};
  transition: all 0.3s;
`;

const UrlInputGroup = styled.div`
  display: flex;
  align-items: stretch;
  flex: 1;
  min-width: 0;
  border: 1.5px solid ${colors.primary.withOpacity(0.4).hex};
  border-radius: 0.8rem;
  overflow: hidden;
  background: ${colors.background.hex};
  height: 3.6rem;

  &:focus-within {
    border-color: ${colors.primary.hex};
    box-shadow: 0 0 0 3px ${colors.primary.withOpacity(0.15).hex};
  }
`;

const UrlPrefix = styled.span`
  display: flex;
  align-items: center;
  padding: 0 0.8rem 0 1rem;
  font-size: 1.2rem;
  color: ${colors.text.withOpacity(0.6).hex};
  border-right: 1px solid ${colors.grey.hex};
  white-space: nowrap;

  @media (max-width: 75em) {
    display: none;
  }
`;

const UrlInput = styled.input.attrs({ type: "text" })`
  border: none;
  outline: none;
  background: transparent;
  font-size: 1.35rem;
  font-weight: 500;
  color: ${colors.text.hex};
  padding: 0 1rem;
  width: 100%;
  min-width: 0;
`;

const ShareButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 3.2rem;
  width: 3.2rem;
  border-radius: 50%;
  background: ${colors.primary.hex};
  color: ${colors.white.hex};
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover {
    background: ${colors.primary.withOpacity(0.9).hex};
  }
`;

const ProfileBody = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 2rem;
  width: 100%;
  align-items: flex-start;

  @media (max-width: 36em) {
    grid-template-columns: minmax(0, 1fr);
    gap: 1.4rem;
  }
`;

const AvatarColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-upload-list-picture-card .ant-upload-list-item-container,
  .ant-upload-select,
  .avatar-uploader > .ant-upload {
    width: 9rem !important;
    height: 9rem !important;
  }

  @media (max-width: 36em) {
    align-items: flex-start;
  }
`;

const AvatarLabel = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${colors.text.withOpacity(0.65).hex};
`;

const ProfileFields = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`;

const SocialList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  width: 100%;
`;

const SocialRow = styled.div`
  display: grid;
  grid-template-columns: 13rem minmax(0, 1fr);
  gap: 1rem;
  align-items: center;
  width: 100%;

  .ant-form-item {
    margin-bottom: 0;
  }

  @media (max-width: 36em) {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.6rem;
  }
`;

const SocialBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.6rem 1rem;
  border-radius: 0.8rem;
  background: ${colors.lightBackground.hex};
  border: 1.5px solid ${colors.primary.withOpacity(0.2).hex};
  height: 3.6rem;
`;

const SocialLabel = styled.span`
  font-size: 1.35rem;
  font-weight: 500;
  color: ${colors.text.hex};
`;

const SocialPrefix = styled.span`
  font-size: 1.2rem;
  color: ${colors.text.withOpacity(0.55).hex};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 2rem;
  row-gap: 0;
  width: 100%;

  @media (max-width: 50em) {
    grid-template-columns: minmax(0, 1fr);
    column-gap: 0;
  }
`;

const FormItem = styled(Form.Item)<{
  $span?: 1 | 2;
  $noLabel?: boolean;
  $disabled?: boolean;
}>`
  width: 100%;
  margin-bottom: 2rem;
  grid-column: ${({ $span }) => ($span === 2 ? "1 / -1" : "auto")};

  ${({ $noLabel }) =>
    $noLabel
      ? `.ant-form-item-label { display: none; }`
      : ""}

  ${({ $disabled }) => ($disabled ? "opacity: 0.6; pointer-events: none;" : "")}

  @media (max-width: 50em) {
    grid-column: 1 / -1;
  }

  .ant-form-item-label {
    padding-bottom: 0.4rem;

    > label {
      font-size: 1.4rem;
      height: auto;
      line-height: 1.4;
    }
  }

  .ant-form-item-explain,
  .ant-form-item-extra {
    font-size: 1.2rem;
    min-height: auto;
    line-height: 1.4;
    padding-top: 0.4rem;
  }

  .ant-input,
  .ant-select-selector,
  .ant-picker {
    font-size: 1.4rem;
  }
`;

const ListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  width: 100%;
`;

const EntryCard = styled.div`
  border: 1.5px solid ${colors.primary.withOpacity(0.2).hex};
  border-radius: 0.8rem;
  padding: 1.6rem;
  background: ${colors.lightBackground.hex};
`;

const EntryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.2rem;
`;

const EntryIndex = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.text.withOpacity(0.55).hex};
`;

const RemoveBtn = styled.button.attrs({ type: "button" })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.2rem;
  height: 3.2rem;
  border: none;
  background: transparent;
  border-radius: 50%;
  font-size: 1.6rem;
  color: ${colors.error.hex};
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover {
    background: ${colors.error.withOpacity(0.1).hex};
  }
`;

const AddButton = styled(Button)`
  flex-shrink: 0;
  height: 3.4rem;
  padding: 0 1.4rem;
  font-size: 1.3rem;
  font-weight: 500;
  border-radius: 0.8rem;
`;
